import {
  pendingAuthorizationDtoSchema,
  authorizationDecisionDtoSchema,
  type PendingAuthorizationDto,
  type AuthorizationDecisionDto,
} from '@lib/dto/oauth.dto'
import type { ApproveAuthorizationInput } from '@lib/input/oauth.input'
import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import type { IOAuthService } from '@/types/OAuthService.types'

/**
 * The calls the consent screen makes.
 *
 * Both responses are parsed against the shared schema at the boundary, as every
 * service here does — `redirectTo` especially, because the screen navigates the
 * browser to it. A value this app sends somebody to should be proved a URL
 * before it is used as one, even though the API matched it against the client's
 * registration long before this request existed.
 */
class OAuthServiceImpl implements IOAuthService {
  async pending(requestId: string): Promise<PendingAuthorizationDto> {
    try {
      return await api.get(
        `/oauth/authorization-requests/${encodeURIComponent(requestId)}`,
        pendingAuthorizationDtoSchema,
      )
    } catch (cause) {
      logRequestFailure('Failed to read authorization request', { requestId }, cause)
      throw cause
    }
  }

  async approve(
    requestId: string,
    input: ApproveAuthorizationInput,
  ): Promise<AuthorizationDecisionDto> {
    try {
      return await api.post(
        `/oauth/authorization-requests/${encodeURIComponent(requestId)}/approve`,
        input,
        authorizationDecisionDtoSchema,
      )
    } catch (cause) {
      logRequestFailure('Failed to approve authorization', { requestId }, cause)
      throw cause
    }
  }

  async deny(requestId: string): Promise<AuthorizationDecisionDto> {
    try {
      return await api.post(
        `/oauth/authorization-requests/${encodeURIComponent(requestId)}/deny`,
        {},
        authorizationDecisionDtoSchema,
      )
    } catch (cause) {
      logRequestFailure('Failed to decline authorization', { requestId }, cause)
      throw cause
    }
  }
}

export const OAuthService: IOAuthService = new OAuthServiceImpl()
